import React, { useEffect, useState } from 'react';
import { BasisTheoryProvider, useBasisTheory } from '@basis-theory/react-elements';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { Typography } from '@material-ui/core';
import config from 'aws-exports';

import { useQuery } from '@apollo/client';

import { Loading } from 'core/components/loading';
import { useBasisTheoryElementRefsProvider } from 'common/hooks/basis-theory-elements.context';
import SetupForm from './setup-form';
import ManageAccount from './manage-account';
import { GET_SWEEP_ACCOUNT } from './sweep-account.queries';
import useStyles from './sweep-account.styles';

const stripePromise = loadStripe(config.stripeKey);

/**
 * Independent-agent page for managing the agency's sweep account (one Stripe Customer
 * per affinity stem). If no account exists yet, shows the setup form; otherwise shows
 * the payment-method manager. Gated to independent agents + enabled brands by the
 * account menu / route. (#33155)
 */
const SweepAccount = () => {
  const classes = useStyles();
  const { bt } = useBasisTheory(config.basisTheoryApiKey, {
    elements: true,
    environment: config.basisTheoryElementsEnv
  });
  const { Provider: BasisTheoryElementsProvider, refs: elementRefs } = useBasisTheoryElementRefsProvider();
  // Sweep account/payment-method data is mutated server-side and re-read immediately,
  // so skip the Apollo cache (same rationale as the offer/policy billing flows).
  const { data, loading } = useQuery(GET_SWEEP_ACCOUNT, { fetchPolicy: 'no-cache' });
  const [account, setAccount] = useState(null);

  useEffect(() => {
    if (data) {
      setAccount(data.getSweepAccount);
    }
  }, [data]);

  return (
    <BasisTheoryProvider bt={bt}>
      <BasisTheoryElementsProvider value={elementRefs}>
        <Elements stripe={stripePromise}>
          <div className={classes.container}>
            <Typography variant="h5" className={classes.title}>
              Sweep account
            </Typography>
            <Typography variant="body2" className={classes.subtitle}>
              Pay for new business and manual payments from your agency&apos;s Stripe account.
            </Typography>
            {loading && !account ? (
              <Loading noBackground />
            ) : account ? (
              <ManageAccount account={account} onUpdated={setAccount} />
            ) : (
              <SetupForm onCreated={setAccount} />
            )}
          </div>
        </Elements>
      </BasisTheoryElementsProvider>
    </BasisTheoryProvider>
  );
};

export default SweepAccount;
